import { z } from 'zod';

// ---------------------------------------------------------------------------
// realization.upload.requested — emitted by admin after staging the raw file
// in Storage (bucket `wandero`, `staging/realizations/{uuid}.{ext}`).
//
// Distinct from upload.process.requested (T5, WS6 §5.4): that handler creates
// a NEW `wallpapers` row from scratch (mass-upload of catalog originals) —
// wrong shape for attaching a real customer photo to an EXISTING wallpaper.
// This handler still reuses the SAME sanitizeOriginal() step (privacy-mandatory
// strip of EXIF/GPS/ICC — idea 2026-07-03-wandero-upload-strip-metadata-light-
// compression) before writing the final object.
// ---------------------------------------------------------------------------

export const realizationUploadRequestedSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  /** Staging key in the `wandero` bucket, e.g. 'staging/realizations/<uuid>.jpg'. */
  storage_path: z.string().min(1),
  original_filename: z.string().min(1),
  content_type: z.string().min(1),
  size_bytes: z.number().int().positive(),
  /** Honest caption (operator/client-provided). Plain text — never rendered as HTML. */
  caption: z.string().max(500).nullable().optional(),
  author_name: z.string().max(200).nullable().optional(),
  rating: z.number().int().min(1).max(5).nullable().optional(),
  review_text: z.string().max(2000).nullable().optional(),
});

export type RealizationUploadRequestedPayload = z.infer<typeof realizationUploadRequestedSchema>;

// ---------------------------------------------------------------------------
// realization.upload.processed — emitted by worker handler on success or error
// ---------------------------------------------------------------------------

export const realizationUploadProcessedSuccessSchema = z.object({
  realization_id: z.number().int().positive(),
  wallpaper_id: z.number().int().positive(),
  /** Final WS5-conventional path: interiors/{kw}-realizacja-{N}-{wallpaperId}.webp */
  storage_path: z.string(),
  approved: z.literal(false),
});

export const realizationUploadProcessedErrorSchema = z.object({
  error: z.string(),
  storage_path: z.string(),
});

export const realizationUploadProcessedSchema = z.union([
  realizationUploadProcessedSuccessSchema,
  realizationUploadProcessedErrorSchema,
]);

export type RealizationUploadProcessedPayload = z.infer<typeof realizationUploadProcessedSchema>;
